/**
 * Feed Field Rules Service
 *
 * Manages feed field transformation rules via the ISE backend draft system.
 * Rules are saved as drafts and promoted to JAMS on "Publish Changes".
 *
 * Example rule: Feed=vauto, if Features contains "sunroof", replace with "moon roof"
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MatchOperator =
  | 'equals'
  | 'in'
  | 'contains'
  | 'startsWith'
  | 'greaterThan'
  | 'lessThanOrEqual'
  | 'regex'

export interface FeedFieldMatch {
  field: string
  operator: MatchOperator
  value: string | string[]
}

export type FeedFieldTransformActionType = 'set' | 'replace' | 'append' | 'remove'

export interface FeedFieldTransformAction {
  type: FeedFieldTransformActionType
  value?: string   // for set / append / remove
  find?: string    // for replace
  replaceWith?: string // for replace
}

export interface FeedFieldRule {
  targetField: string
  targetFieldLabel?: string
  match: FeedFieldMatch[]
  logic?: 'AND' | 'OR'
  action: FeedFieldTransformAction
}

export interface FeedFieldRuleConfig {
  feed: string
  rules: FeedFieldRule[]
}

export interface AccountFeedFieldRules {
  feeds: FeedFieldRuleConfig[]
}

// ---------------------------------------------------------------------------
// Fallback feeds (used when the /dealer-feeds API is unavailable)
// ---------------------------------------------------------------------------

export const KNOWN_FEEDS: { value: string; label: string }[] = [
  { value: 'vauto', label: 'vAuto' },
  { value: 'fdds1', label: 'FDDS1 (Ford)' },
  { value: 'fdds2', label: 'FDDS2 (Ford)' },
  { value: 'homenet', label: 'HomeNet' },
  { value: 'locate', label: 'Locate' },
  { value: 'icc', label: 'ICC' },
  { value: 'idms', label: 'IDMS' },
  { value: 'dealerslink', label: 'DealersLink' },
  { value: 'chrome', label: 'Chrome' },
]

// ---------------------------------------------------------------------------
// Match operators
// ---------------------------------------------------------------------------

export const MATCH_OPERATORS: { value: MatchOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'in', label: 'Is one of' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThanOrEqual', label: 'Less than or equal' },
  { value: 'regex', label: 'Regex' },
]

// ---------------------------------------------------------------------------
// Transform action types
// ---------------------------------------------------------------------------

export const TRANSFORM_ACTIONS: { value: FeedFieldTransformActionType; label: string; description: string }[] = [
  { value: 'replace', label: 'Replace text', description: 'Find and replace text within the field value' },
  { value: 'set', label: 'Set value', description: 'Overwrite the field with a fixed value' },
  { value: 'append', label: 'Append value', description: 'Add a value to the field (or array)' },
  { value: 'remove', label: 'Remove value', description: 'Remove a value from the field (or array)' },
]

// ---------------------------------------------------------------------------
// Account feeds — dynamic list from /dealer-feeds API
// ---------------------------------------------------------------------------

export async function fetchAccountFeeds(
  iseEndpoint: string,
  accountId: string,
  token: string,
): Promise<{ value: string; label: string }[]> {
  const base = String(iseEndpoint || '').replace(/\/+$/g, '')
  const url = `${base}/dealer-feeds/${encodeURIComponent(accountId)}`
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!resp.ok) return []
  const json = (await resp.json().catch(() => ({}))) as Record<string, unknown>
  const feeds = json?.feeds
  if (!Array.isArray(feeds)) return []
  return feeds
    .filter((f: any) => f && typeof f === 'object' && f.id)
    .map((f: any) => ({
      value: f.id.startsWith('import_') ? f.id.slice('import_'.length) : String(f.id),
      label: String(f.friendlyName || f.id || ''),
    }))
    .filter((f) => f.value.length > 0)
}

// ---------------------------------------------------------------------------
// API — ISE backend draft endpoints
// ---------------------------------------------------------------------------

export async function fetchFeedFieldRules(
  iseEndpoint: string,
  accountId: string,
  token: string,
): Promise<AccountFeedFieldRules & { source?: 'draft' | 'published' | 'none' }> {
  const base = String(iseEndpoint || '').replace(/\/+$/g, '')
  const url = `${base}/feed-condition-rules/${encodeURIComponent(accountId)}?configType=feedFieldRules&includeDraft=true`
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (resp.ok) {
    const json = await resp.json()
    const data = json?.data ?? json ?? {}
    return {
      feeds: Array.isArray(data.feeds) ? data.feeds : [],
      source: data.source,
    }
  }

  if (resp.status === 404 || resp.status === 403 || resp.status === 400) {
    return { feeds: [], source: 'none' }
  }

  throw new Error(`Failed to fetch feed field rules: ${resp.status}`)
}

export async function saveFeedFieldRulesDraft(
  iseEndpoint: string,
  accountId: string,
  token: string,
  rules: AccountFeedFieldRules,
): Promise<boolean> {
  const base = String(iseEndpoint || '').replace(/\/+$/g, '')
  const url = `${base}/feed-condition-rules/${encodeURIComponent(accountId)}?configType=feedFieldRules`
  const resp = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(rules),
  })
  return resp.ok
}

export async function deleteFeedFieldRulesDraft(
  iseEndpoint: string,
  accountId: string,
  token: string,
): Promise<boolean> {
  const base = String(iseEndpoint || '').replace(/\/+$/g, '')
  const url = `${base}/feed-condition-rules/${encodeURIComponent(accountId)}?configType=feedFieldRules`
  const resp = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return resp.ok
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function createEmptyFeedConfig(feed: string): FeedFieldRuleConfig {
  return { feed, rules: [] }
}

export function createEmptyRule(): FeedFieldRule {
  return {
    targetField: '',
    targetFieldLabel: '',
    match: [createEmptyMatch()],
    logic: 'AND',
    action: { type: 'replace', find: '', replaceWith: '' },
  }
}

export function createEmptyMatch(): FeedFieldMatch {
  return { field: '', operator: 'contains', value: '' }
}

export function getFeedLabel(
  feedValue: string,
  dynamicFeeds?: { value: string; label: string }[],
): string {
  return dynamicFeeds?.find(f => f.value === feedValue)?.label
    ?? KNOWN_FEEDS.find(f => f.value === feedValue)?.label
    ?? feedValue
}
